import { TitleImagesAndPDFs } from '../../backbone/features/mobileForm/BaseField/models';
import { MobileFormStructure } from '../../backbone/features/mobileForm/MobileFormStructure/models';
import { MobileMediaField } from '../../backbone/shared/models/MobileMediaField';
import { CooldownForTask } from '../sensors/models';
import {
    BaseBatchTask,
    BaseTask,
    BaseTaskSource,
    BaseZenputCollection,
    BaseZenputModel,
    Followers,
    Paginator,
    Project,
    Projects,
    RecurringProject,
    TaskFulfillment,
    Tasks,
    User,
    UsersDistributeGroupsAndRoles
} from '../shared/forms';

const MobileTaskMixin = {
    initialize: function (attrs, options) {
        if (options.parent_project) {
            this.parent_project = options.parent_project; // this is needed by this.create_image_field_model() below
        }

        if (
            !this.get('title_image_field') &&
            this.isNew() &&
            this.get('reply') &&
            this.get('reply').get('type') != 'form_submission'
        ) {
            if (!this.create_image_field_model) {
                throw new Error(
                    'Programmer Error: create_image_field_model() not implemented in ' +
                        'MobileTaskMixin implementation'
                );
            }

            this.set('title_image_field', this.create_image_field_model(), { silent: true });
        }

        if (this.isNew()) {
            this.set('fulfillment', null, { silent: true });
        }
    },

    isNew: function () {
        if (this.get('local_guid')) {
            return true;
        }

        return MobileTask.__super__.isNew.apply(this, arguments);
    },

    _convert_json_to_models: function () {
        if (this.get('title_image_field') && !(this.get('title_image_field') instanceof Backbone.Model)) {
            this.set(
                'title_image_field',
                new MediaFieldTask(this.get('title_image_field'), {
                    task_or_project: this instanceof BatchTask ? this.parent_project : this
                }),
                { silent: true }
            );
        }
    }
};

/** This collection handles when we need to display or hold a subset of fields from a submission **/
export const GenericImageFields = BaseZenputCollection.extend({
    model: MobileMediaField
});

/** Mobile implementation of TaskSource **/
export const TaskSource = BaseTaskSource.extend({
    defaults: _.extend({}, BaseTaskSource.prototype.defaults, {
        triggering_image_fields: null // GenericImageFields
    }),

    // note this is overwriting the submodels of BaseTaskSource since we are using the mobile versions now.
    submodels: {
        form_submission: MobileFormStructure,
        triggering_image_fields: GenericImageFields
    }
});

export const MobileTaskFulfillment = TaskFulfillment.extend({
    _convert_json_to_models: function () {
        var this_ = this;
        $.each(this.get('fields'), function (i, field) {
            if (!(field instanceof Backbone.Model)) {
                this_.get('fields')[i] = new MediaFieldTask(field, {
                    task_or_project: this_.parent_task
                });
            }
        });
    }
});

export const MobileTask = BaseTask.extend(
    {
        // mixins: MobileTaskMixin

        task_source_klass: TaskSource,
        fast_tasks_model: null, // FastTasks

        submodels: $.extend(
            {
                cooldown: CooldownForTask
            },
            BaseTask.prototype.submodels
        ),

        initialize: function (attributes, options) {
            options = options || {};
            this.fast_tasks_model = options.fast_tasks_model || null;

            MobileTask.__super__.initialize.apply(this, arguments);
        },

        sync: function (method, model, options) {
            if (method == 'update' && !this.get('is_edit_mode')) {
                options.url = `${model.url}/fulfill/`;
                return Backbone.sync(method, model, options);
            }

            return MobileTask.__super__.sync.apply(this, arguments);
        },

        _convert_json_to_models: function () {
            if (this.get('fulfillment') && !(this.get('fulfillment') instanceof Backbone.Model)) {
                this.set(
                    'fulfillment',
                    new MobileTaskFulfillment(this.get('fulfillment'), {
                        parent_task: this
                    }),
                    { silent: true }
                );
            }

            MobileTask.__super__._convert_json_to_models.apply(this, arguments);

            if (this.get('followers')) {
                this.set('followers', new MobileFollowers(this.get('followers').toJSON()), { silent: true });
            }
            // Set defaults for fulfillment if they don't exist for already created tasks and tasks not being edited
            if (!this.get('is_edit_mode')) {
                this.set_default_fulfillment();
            }
            // Title images for mobile should be TitleImagesAndPDFs instead of Images
            if (this.get('title_image')) {
                this.set('title_image', new TitleImagesAndPDFs(this.get('title_image').toJSON()), { silent: true });
            }
        },

        create_image_field_model: function () {
            return new MediaFieldTask(
                {
                    id: 1,
                    type: 'image'
                },
                {
                    task_or_project: this
                }
            );
        },

        set_default_fulfillment: function () {
            if (!this.get('fulfillment') && this.get('reply') && !this.isNew()) {
                var reply = this.get('reply');
                this.set(
                    'fulfillment',
                    new TaskFulfillment(
                        {
                            type: reply.get('type')
                        },
                        { parent_task: this }
                    )
                );
                var fulfillment = this.get('fulfillment');

                if (reply.get('form_template')) {
                    fulfillment.set('form_template', reply.get('form_template'));
                }
                if (fulfillment.get('type') == 'quick') {
                    var image_field = this.create_image_field_model();
                    image_field.set({
                        title: 'Please provide a photo:',
                        required: true
                    });
                    fulfillment.get('fields').push(image_field);
                }
            }
        },

        save: function () {
            // if it isn't a new task, fill in fulfillment before saving
            if (this.isNew() || this.get('is_edit_mode')) {
                this.set('fulfillment', null);
                return MobileTask.__super__.save.apply(this, arguments);
            }

            if (!$f.active_user.is_logged_in()) {
                return MobileTask.__super__.save.apply(this, arguments);
            }

            this.get('fulfillment').set({
                date_completed: { $date: new Date().getTime() },
                user: { id: $f.active_user.get('id') }
            });
            MobileTask.__super__.save.apply(this, arguments);
        },

        get_date_due: function () {
            if (!this.get('date_due')) {
                return null;
            }

            var time_zone = this.get('time_zone') || moment.tz.guess();
            return moment(this.get('date_due').$date).tz(time_zone);
        },

        get_date_completed: function () {
            if (!this.get('date_submitted')) {
                return null;
            }

            var time_zone = this.get('time_zone') || moment.tz.guess();
            return moment(this.get('date_submitted').$date).tz(time_zone);
        },

        get_date_start: function () {
            if (!this.get('date_start')) {
                return null;
            }

            var time_zone = this.get('time_zone') || moment.tz.guess();
            return moment(this.get('date_start').$date).tz(time_zone);
        },

        is_missed: function () {
            const date_due = this.get_date_due();

            if (this.get('current_state') === 'incomplete_and_closed') {
                return true;
            }

            if (!date_due) {
                return false;
            }

            if (!this.get('project')) {
                return false;
            }

            const is_auto_archive = this.get('project').get('is_auto_archive');
            const now = moment();

            if (is_auto_archive && now.isAfter(date_due)) {
                return true;
            }

            return false;
        },

        is_task_all_day: function () {
            if (!this.is_task_started_and_due_within_same_day()) {
                return false;
            }

            var start_of_date_due = $f.utils.get_start_of_day(this.get_date_due());
            var end_of_date_due = $f.utils.get_end_of_day(this.get_date_due());
            var date_start = this.get_date_start();
            var date_due = this.get_date_due();

            return start_of_date_due === date_start && end_of_date_due === date_due;
        },

        is_task_started_and_due_within_same_day: function () {
            var date_start = this.get_date_start();
            var date_due = this.get_date_due();

            if (!date_due) {
                return false;
            }

            return date_due.dayOfYear() === date_start.dayOfYear();
        },

        is_task_started_and_completed_within_same_day: function () {
            var date_start = this.get_date_start();
            var date_completed = this.get_date_completed();

            if (!date_completed) {
                return false;
            }

            return date_completed.dayOfYear() === date_start.dayOfYear();
        },

        is_task_due_today: function () {
            var today = moment();
            var date_due = this.get_date_due();

            if (!date_due) {
                return false;
            }

            return date_due.dayOfYear() === today.dayOfYear();
        },

        is_task_due_in_one_hour: function () {
            var now = moment();
            var date_due = this.get_date_due();

            if (!date_due) {
                return false;
            }

            return date_due.isBetween(now, moment(now).add(1, 'hour'));
        },

        is_task_complete_today: function () {
            var today = moment();
            var date_completed = this.get_date_completed();

            if (!date_completed) {
                return false;
            }

            return date_completed.dayOfYear() === today.dayOfYear();
        },

        is_archived: function () {
            return !(this.get('is_closed') || this.get('is_active'));
        },

        get_current_state_string: function () {
            let current_state = this.get('current_state');
            let is_completed_late = this.get('is_completed_late');
            let status_strings = {
                open: _t('mobile_task_status_title_open'),
                processing: _t('mobile_task_status_title_processing'),
                complete: is_completed_late
                    ? _t('mobile_tso_filter_completed_late')
                    : _t('mobile_task_status_title_complete'),
                overdue: _t('mobile_tasks_toolbarnav_header_overdue'),
                incomplete_and_closed: _t('mobile_task_status_title_incomplete_and_closed'),
                complete_with_exception: is_completed_late
                    ? _t('mobile_task_status_title_completed_late_with_exceptions')
                    : _t('mobile_task_status_title_complete_with_exceptions'),
                unavailable: MobileTask.get_available_in_str(this.get_date_start())
            };
            return (
                status_strings[current_state] ||
                $f.mobile.logging.log_error('Unknown task status', { status: current_state })
            );
        },

        get_assigned_or_fulfilled_user_display: function () {
            try {
                return this.get('fulfillment').get('user').display_name;
            } catch (e) {
                if (this.get('assignee')) {
                    return this.get('assignee').get('display_name');
                }
            }

            return null;
        },

        /**
         * Provides a way for us to delete a task locally, removing it both from whatever collection it's a part of and our caches.
         */
        delete_from_collection_and_caches: function (callback) {
            if (this.collection) {
                // if the task is in a collection with a results_count (e.g., the 'overdue' card), decrement that as well
                if (this.collection.paginator && !$f.utils.is_nil(this.collection.paginator.get('results_count'))) {
                    this.collection.paginator.decrease_results_count();
                }

                this.collection.remove(this);
            }

            $f.mobile.cache.remove_ids_by_cache_substring({
                ids: this.id,
                cache_key_substrs: MobileTasks.cache_denylist_key,
                callback: callback
            });
        },

        /**
         * Update task on disk in our caches
         */
        persist_updates_to_caches: function (callback) {
            var this_ = this;

            $f.mobile.cache.update_cache_item({
                item_id: this.id,
                cache_key_substrs: MobileTasks.cache_denylist_key,
                callback: callback,
                update_fn: function (old_item) {
                    return this_;
                }
            });
        },

        mark_as_complete_in_caches: function (callback) {
            if (this.collection && this.collection.paginator.get('status') === 'active_incomplete') {
                this.collection.paginator.decrease_results_count();
            }

            this.set('current_state', 'complete');
            this.persist_updates_to_caches(callback);
        },

        mark_as_archived_in_caches: function (callback) {
            this.set('current_state', 'incomplete_and_closed');
            this.persist_updates_to_caches(callback);
        },

        can_start: function () {
            if (this.get('current_state') !== 'unavailable') {
                return true;
            }
            // The server is deciding when tasks are "open" vs "unavailable,"
            // so if mobile doesn't fetch new data (e.g., the user's offline),
            // it will block tasks that have recently changed from upcoming → open.
            // When we make a new Tasks v2 API and rewrite these pages in React,
            // this is something we should consider.
            var date_start = this.get('date_start').$date;
            return new Date().getTime() > date_start;
        }
    },
    {
        get_available_in_str: function (date_start) {
            // date_start: moment datetime that the task starts
            let now = moment();

            let years = date_start.diff(now, 'years');
            if (years === 1) {
                return _t('mobile_tasks_bucket_available_in_one_year');
            } else if (years > 1) {
                return _t('mobile_tasks_bucket_available_in_years', { years });
            }

            let months = date_start.diff(now, 'months');
            if (months === 1) {
                return _t('mobile_tasks_bucket_available_in_one_month');
            } else if (months > 1 && months < 12) {
                return _t('mobile_tasks_bucket_available_in_months', { months });
            }

            let weeks = date_start.diff(now, 'weeks');
            if (weeks === 1) {
                return _t('mobile_tasks_bucket_available_in_one_week');
            } else if (weeks > 1 && weeks <= 4) {
                return _t('mobile_tasks_bucket_available_in_weeks', { weeks });
            }

            let days = date_start.diff(now, 'days');
            if (days === 1) {
                return _t('mobile_tasks_bucket_available_in_one_day');
            } else if (days > 1 && days < 7) {
                return _t('mobile_tasks_bucket_available_in_days', { days });
            }

            let hours = date_start.diff(now, 'hours');
            if (hours === 1) {
                return _t('mobile_tasks_bucket_available_in_one_hour');
            } else if (hours > 1 && hours < 24) {
                return _t('mobile_tasks_bucket_available_in_hours', { hours });
            }

            let minutes = date_start.diff(now, 'minutes');
            if (minutes === 1) {
                return _t('mobile_tasks_bucket_available_in_one_minute');
            } else if (minutes > 1 && minutes < 60) {
                return _t('mobile_tasks_bucket_available_in_minutes', { minutes });
            }

            let seconds = date_start.diff(now, 'seconds');
            if (seconds < 60) {
                return _t('mobile_tasks_bucket_available_in_one_minute');
            }

            // Default value, we shouldn't get here.
            return _t('mobile_task_status_title_upcoming');
        }
    }
);

export const BatchTask = BaseBatchTask.extend({
    // mixins: MobileTaskMixin

    parent_project: null, // MobileProject

    initialize: function (attributes, options) {
        options = options || {};
        $.extend(this, options);

        BatchTask.__super__.initialize.apply(this, arguments);

        this.parent_project = options.parent_project || null;
    },

    validation: $.extend(
        {
            distribute_group: { fn: '_validate_distribute_group' }
        },
        BaseBatchTask.prototype.validation
    ),

    _validate_distribute_group: function (value, attr, computed_state) {
        if (!value.get('id')) {
            return 'Please select a team.';
        }
    },

    create_image_field_model: function () {
        return new MediaFieldTask(
            {
                id: 1,
                type: 'image'
            },
            {
                task_or_project: this.parent_project
            }
        );
    }
});

Cocktail.mixin(MobileTask, MobileTaskMixin);
Cocktail.mixin(BatchTask, MobileTaskMixin);

export const MobileTasks = Tasks.extend(
    {
        // @author:gustavo we are clearing this cache upon task creation and task completion to avoid issues related to
        // stale results after completing or creating tasks.
        cache_enabled: true,
        cache_ttl: 1000, // 1 sec
        cache_key: null, // set in get_cache_key
        default_limit: 20,
        cache_fetch_limit: 20,
        model: MobileTask,

        // We are overwriting this initialize to instantiate a regular Paginator class
        // We do not want to overwrite the is_active filter on mobile
        initialize: function (models, options) {
            var this_ = this;
            options = options || {};
            options.paginator_options = options.paginator_options || {};
            options.paginator_options.limit = options.paginator_options.limit || this.default_limit;
            $.extend(this, _.omit(options, 'search_options'));

            // @author:david because we have a cache_ttl of 1 sec, every time we paginate down the list, we'll be fetching
            // the full cache_fetch_limit (even if we only return the front-end the 20 results we request). To fix this
            // (at least for now), we'll fetch results in sets of this.cache_fetch_limit. @jira:MOBILE-622
            this.paginator_options.limit = this.cache_fetch_limit;
            if (!this.paginator) {
                if (options && options.search_options) {
                    _.extend(this_.paginator_options, options.search_options);
                }
                this.paginator = new Paginator(this_.paginator_options);
                // TODO - search_options is being passed in either as a primitive, or as an instance of BaseSearchOptions
                if (options.search_options && options.search_options.update_paginator) {
                    options.search_options.update_paginator(this.paginator);
                }
            }

            // If it was initialized without SearchOptions
            // Our global paginator has a default order_by of 'name', which isn't applicable to all tasks. Remove it.
            if (this.paginator.get('order_by') === 'name') {
                this.paginator.unset('order_by');
            }

            this.listenTo(this.paginator, 'change:results_count', function () {
                this_._persist_results_count_to_cache();
            });

            return MobileTasks.__super__.initialize.apply(this, arguments);
        },

        fetch_more_for_bucket: function (success, error) {
            this.paginator.next_page();
            const previousLimit = this.paginator.get('limit');
            this.paginator.set('limit', 20);
            this.fetch({
                remove: false,
                success,
                error: () => {
                    this.paginator.set('limit', previousLimit);
                    this.paginator.previous_page();
                    error();
                }
            });
        },

        comparator: function (model) {
            return; // always trust server ordering
        },

        _persist_to_cache: function (callback) {
            MobileTasks.__super__._persist_to_cache.call(this, callback);

            if ($f.flags.get_flag('use_local_notifications') && $f.active_user.get('local_notifications_enabled')) {
                const tasks = this.models.map((model) => model.toJSON());
                const tasksWithAssignees = tasks.map((task) => ({
                    ...task,
                    assignee: task.assignee.toJSON()
                }));
                $f.mobile.notifications.scheduleTaskDueSoonLocalNotifications(tasksWithAssignees);
            }
        },

        parse: function (response) {
            var this_ = this;
            var results = MobileTasks.__super__.parse.apply(this, arguments);

            if (this_.paginator.get('status') !== 'open') {
                // don't need to strip anything out if we're not showing open tasks
                return results;
            }

            // Throw out any tasks that are already in pending task uploads or pending form uploads
            results = $.grep(results, function (result, i) {
                try {
                    var denylist_timestamp = $f.mobile.cache.denylist[this_.constructor.cache_denylist_key][result.id];
                } catch (e) {
                    // leave denylist_timestamp null, we will null-check it below
                }

                // if denylist for result was entered less than an hour ago, discard the result
                // @author:david someday it would be nice to share this with any other collections that would need it,
                // but for now, Tasks is the only collection we need to do this for.
                if (denylist_timestamp && denylist_timestamp > new Date().getTime() - 3600000) {
                    return false; // was already submitted, remove from results
                }

                return true; // keep in results
            });

            return results;
        },

        // we are going to set the cache_key at fetch time dynamically
        fetch: function (options) {
            this.cache_key = this.get_cache_key(options);
            this._load_results_count_from_cache();
            return MobileTasks.__super__.fetch.apply(this, arguments);
        },

        /**
         * Set the cache key for this search options/tasks
         *
         * Need to make cache_key unique based on search_criteria, paginator and dashboard_filters
         */
        get_cache_key: function (options) {
            // build an array of the attribute:value's we want to be considered for the cache key
            var cache_key_attributes = [];
            _.each(this.paginator.toJSON(), function (val, key) {
                if (!val) {
                    return true;
                }

                // we don't want these attributes to contribute to a unique cache key
                if (
                    _.contains(
                        [
                            'start',
                            'limit',
                            'search_query', // cache isn't used when that's added anyway
                            'search_by',
                            'results_count',
                            'order_by',
                            'num_selected',
                            'tz'
                        ],
                        key
                    )
                ) {
                    return true; // continue
                }

                if (typeof val === 'object') {
                    val = JSON.stringify(val);
                }

                cache_key_attributes.push(`${key}:${val}`);
            });

            cache_key_attributes = cache_key_attributes.sort(); // make sure we're always in the same order

            // hash that array into a cache key
            return `${this.constructor.cache_denylist_key}__${$f.utils.hash_str(cache_key_attributes.join('_'))}`;
        },

        _persist_results_count_to_cache: function () {
            $f.mobile.tasks.results_count_cache.update_results_count(
                this.get_cache_key(),
                this.paginator.get('results_count')
            );
        },

        _load_results_count_from_cache: function () {
            var this_ = this;
            $f.mobile.tasks.results_count_cache.get_results_count(this.get_cache_key(), function (count) {
                this_.paginator.set('results_count', count);
            });
        }
    },
    {
        cache_denylist_key: 'mobile_tasks',

        get_order_by_key_for_status: function (status) {
            return status === 'closed' ? 'date_completed' : 'date_due_local';
        },

        create_collection_using_search_options: function (search_options, options) {
            options = options || {};

            var paginator_options = search_options.get_options_for_paginator();

            if (options.account_id) {
                paginator_options.account_id = options.account_id;
            }

            return new this([], {
                // instantiate a new of whatever our class is (important if we're subclassed)
                paginator_options: paginator_options
            });
        },

        create_collection_for_overdue_bucket: function (search_options, options) {
            options = options || {};

            if ($f.utils.is_nil(options.limit)) {
                throw new Error('Programmer Error: options.limit requied for create_collection_for_overdue_bucket()');
            }

            var collection = this.create_collection_using_search_options(search_options, options);

            var limit = options.limit || collection.paginator.limit;
            var fetch_limit = 23;

            collection.paginator.set({
                bucket: `${search_options.get_value_for_field('status')}_overdue`,
                limit: limit,
                search_query: null
            });

            collection.paginator.unset('status'); // don't need this since we're wrapping it into 'bucket'

            collection.cache_fetch_limit = fetch_limit;

            return collection;
        },

        create_collection_for_today_bucket: function (search_options, options) {
            options = options || {};

            var collection = this.create_collection_using_search_options(search_options, options);

            collection.paginator.set({
                search_query: null,
                bucket: `${search_options.get_value_for_field('status')}_today`
            });

            collection.paginator.unset('status'); // don't need this since we're wrapping it into 'bucket'

            return collection;
        },

        create_collection_for_week_bucket: function (search_options, options) {
            options = options || {};

            var collection = this.create_collection_using_search_options(search_options, options);

            collection.paginator.set({
                bucket: `${search_options.get_value_for_field('status')}_this_week`
            });

            collection.paginator.unset('status'); // don't need this since we're wrapping it into 'bucket'

            return collection;
        },

        create_collection_outside_this_week_bucket: function (search_options, options) {
            options = options || {};

            var collection = this.create_collection_using_search_options(search_options, options);

            var time_bucket = _.contains(
                ['closed', 'archived_incomplete', 'completed_late'],
                search_options.get_value_for_field('status')
            )
                ? 'last_week'
                : 'upcoming';

            collection.paginator.set({
                bucket: `${search_options.get_value_for_field('status')}_${time_bucket}`
            });

            collection.paginator.unset('status'); // don't need this since we're wrapping it into 'bucket'

            return collection;
        },

        _create_collection_for_project_tasks: function (search_options, options) {
            options = options || {};

            var collection = this.create_collection_using_search_options(search_options, options);

            collection.paginator.set({
                search_query: null,
                include_has_exceptions: true
            });
            return collection;
        },

        create_collection_for_project_tasks_complete: function (search_options, options) {
            var collection = this._create_collection_for_project_tasks(search_options, options);
            collection.paginator.set({
                status: 'closed',
                search_query: null,
                order_by: 'date_submitted',
                include_future_tasks_v2: true,
                is_active: 'null', // null has to be a string otherwise the paginator will remove it.
                include_has_exceptions: true
            });
            collection.paginator.default_query_string_attrs.push('is_active');
            return collection;
        },

        create_collection_for_project_tasks_incomplete: function (search_options, options) {
            var collection = this._create_collection_for_project_tasks(search_options, options);

            collection.paginator.set({
                include_future_tasks_v2: true,
                status: 'active_incomplete',
                search_query: null,
                order_by: 'date_due_local',
                include_has_exceptions: true
            });

            return collection;
        },

        create_collection_for_project_tasks_archived_incomplete: function (search_options, options) {
            var collection = this._create_collection_for_project_tasks(search_options, options);

            collection.paginator.set({
                status: 'archived_incomplete',
                search_query: null,
                include_future_tasks_v2: true,
                order_by: 'date_due_local',
                include_has_exceptions: true
            });
            return collection;
        },

        create_collection_for_text_search: function (search_options, options) {
            var collection = this.create_collection_using_search_options(search_options, options);

            const status = collection.paginator.get('status');
            let include_future_tasks_v2 = true;
            let include_has_exceptions = true;
            if (status === 'open_available') {
                include_future_tasks_v2 = false;
                include_has_exceptions = false;
            }

            collection.paginator.set({
                include_has_exceptions: include_has_exceptions,
                include_future_tasks_v2: include_future_tasks_v2
            });

            return collection;
        }
    }
);

export const DashboardTasks = MobileTasks.extend({
    // Allows us to include global group search options when filtering task list. Mobile dashboard uses these instead
    // of offering group filter options in the task tab
    allow_group_id_on_me_filter: true
});

/**
 * We need a special task collection for My Tasks vs Nearby Tasks so we can implement
 * a special parse method for My Tasks that removes completed local tasks that need to be after
 * fetching.
 */
export const MyTasks = MobileTasks.extend({
    // if true, will remove any local_records that are not returned in a fetch. We don't
    // want to do this when we're fetching via infinite scroll, so we will set it false then.
    _remove_cached_on_parse: true,

    parse: function (response) {
        var results = MyTasks.__super__.parse.apply(this, arguments);

        if (!this._remove_cached_on_parse) {
            this._remove_cached_on_parse = true; // reset to default for next time
            return results;
        }

        // @author:david this is using the old cache, not the new one...? I think we need more cleanup around this.
        // Remove any tasks from local storage that are not in results
        var ids_to_remove_locally = [];
        $f.mobile.tasks.each(function (local_task, i) {
            var is_in_results = false;
            $.each(results, function (j, result) {
                if (result.id == local_task.get('id')) {
                    is_in_results = true;
                    return false;
                }
            });
            if (!is_in_results) {
                ids_to_remove_locally.push(local_task.get('id'));
            }
        });
        $f.mobile.tasks.remove_records(ids_to_remove_locally);

        return results;
    },

    fetch: function (options) {
        if (options.remove === false) {
            this._remove_cached_on_parse = false;
        }
        MyTasks.__super__.fetch.apply(this, arguments);
    }
});

/**
 * An image/video field for Tasks or Projects with logic required for uploading.
 */
export const MediaFieldTask = MobileMediaField.extend({
    task_or_project: null, // MobileTask | MobileProject

    initialize: function () {
        MediaFieldTask.__super__.initialize.apply(this, arguments);

        if (!this.task_or_project) {
            throw new Error('Programmer Error: task_or_project not found in MediaFieldTask');
        }
    },

    /**
     * Is this Task or BatchTask being fulfulled, or is it being created?
     */
    _is_fulfilling: function () {
        if (this.task_or_project instanceof MobileProject) {
            return false; // projects are never fulfilled, and their guids are just their IDs
        }

        return !this.task_or_project.isNew();
    },

    get_parent_upload_guid: function () {
        if (this.task_or_project instanceof MobileProject) {
            // we are creating a project
            $f.mobile.uploads.set_model_guid(this.task_or_project);
            return this.task_or_project.id;
        } else if (this.task_or_project.fast_tasks_model) {
            // we are creating tasks
            $f.mobile.uploads.set_model_guid(this.task_or_project.fast_tasks_model);
            return this.task_or_project.fast_tasks_model.id;
        }

        // if there is no fast_tasks_model, we must be editing a task. There is already a unique ID, so we can use that
        return this.task_or_project.id;
    },

    create_field_upload_filename: function (file_entry) {
        if (this._is_fulfilling()) {
            // task is being fulfilled
            return $f.storage.create_s3_filepath(
                $f.active_company.id,
                'tasks/submissions',
                this.task_or_project.id,
                file_entry.name
            );
        }

        // task is being created
        return $f.storage.create_s3_filepath($f.active_company.id, 'tasks/templates', null, file_entry.name);
    }
});

export const MobileFollowers = Followers.extend({
    model: User,

    convert_to_user_distributed: function () {
        var users_distribute_groups_role = new UsersDistributeGroupsAndRoles();
        this.each(function (model) {
            users_distribute_groups_role.add({
                id: `user~${model.id}`,
                raw_id: model.id,
                type: 'user',
                name: model.get_display_name()
            });
        });

        return users_distribute_groups_role;
    }
});

export const FastTasks = BaseZenputModel.extend({
    defaults: {
        id: null,
        tasks: null // MobileTasks
    },

    submodels: {
        tasks: MobileTasks
    },

    url: '/api/v1/tasks',

    sync: function (method, model, options) {
        switch (method) {
            case 'create':
                options.url = `${model.url}/save_fast_tasks/`;
        }

        return Backbone.sync(method, model, options);
    }
});

export const MobileProject = Project.extend({
    submodels: function () {
        return _.extend({}, Project.prototype.submodels, {
            create_tasks_data: BatchTask
        });
    },

    validation: _.extend(RecurringProject.prototype.validation, {
        distribute_group: function () {
            // we are overriding the behavior of non-mobile recurring project to ALWAYS need a distribute group
            if (!this.get('create_tasks_data').get('distribute_group').get('id')) {
                return _t('mobile_projects_team_required');
            }
        },
        date_due_naive: function (value, attr, computedState) {
            if (!this.get('recurring') && !this.get('date_due_naive')) {
                return _t('mobile_projects_due_date_required');
            }
        }
    }),

    initialize: function () {
        if (!this.get('followers')) {
            this.set('followers', new Followers());
        }
    },

    _get_submodeled_attributes: function (attributes) {
        // @author:david we need to hijack our submodeling of 'create_tasks_data' to make sure it has a parent_project
        // property, which it needs to give its MediaField for file naming, etc. This is a problem
        // when this is deserialized from disk and submodels are created.
        if (attributes.create_tasks_data && !(attributes.create_tasks_data instanceof Backbone.Model)) {
            var submodels = this._get_submodels();
            attributes.create_tasks_data = new submodels.create_tasks_data(attributes.create_tasks_data, {
                parent_project: this
            });
        }

        return MobileProject.__super__._get_submodeled_attributes.call(this, attributes);
    }
});

export const MobileProjects = Projects.extend({
    group_name: null, // Need group name to pass to filter options page on mobile

    initialize: function (model, options) {
        MobileProjects.__super__.initialize.apply(this, arguments);
        this.paginator.set('order_by', 'date_due_local');
    }
});
